import {Action, createReducer, on} from '@ngrx/store';
import {
  approvedOrganizationChangeSuccessAction,
  getMultipleOrganizationsAction,
  getMultipleOrganizationsFailureAction,
  getMultipleOrganizationsSuccessAction,
  getOrganizationAction,
  getOrganizationFailureAction,
  getOrganizationSuccessAction,
  getSupervisorOrganizationsAction,
  getSupervisorOrganizationsFailureAction,
  getSupervisorOrganizationsSuccessAction,
  resetSuccessfulUpdateAction,
  updateOrganizationAction,
  updateOrganizationFailureAction,
  updateOrganizationSuccessAction
} from './organization.actions';
import {IOrganizationState} from './organization.state';
import {stateHelperFunctions} from '../stateHelperFunctions';

export const initialOrganizationState: IOrganizationState = {
  organizations: [],
  loadingOrganizations: [],
  supervisorOrganizations: undefined,
  error: undefined,
  isUpdating: false,
  isUpdateSuccessful: false,
}

const organizationReducer = createReducer(
  initialOrganizationState,

  on(getOrganizationAction, (state, action): IOrganizationState => {
      return stateHelperFunctions.hasItem(state, action.id) ? state : ({
        ...state,
        loadingOrganizations: state.loadingOrganizations.concat([action.id])
      })
    }
  ),

  on(updateOrganizationAction, (state, action): IOrganizationState => {
    let loadingOrganizations = state.loadingOrganizations;

    if (!stateHelperFunctions.hasItem(state, action.data.id)) {
      loadingOrganizations = loadingOrganizations.concat([action.data.id]);
    }

    return {
      ...state,
      isUpdating: true,
      isUpdateSuccessful: false,
      loadingOrganizations
    };
  }),

  on(getOrganizationSuccessAction, (state, action): IOrganizationState => ({
      ...state,
      loadingOrganizations: stateHelperFunctions.removeLoadingId(state.loadingOrganizations, action.data.id),
      organizations: state.organizations?.concat(action.data)
    })
  ),

  on(getOrganizationFailureAction,
    updateOrganizationFailureAction,
    (state, action): IOrganizationState => ({
      ...state,
      isUpdating: false,
      isUpdateSuccessful: false,
      loadingOrganizations: stateHelperFunctions.removeLoadingId(state.loadingOrganizations, action.id),
      error: action.error.error
    })
  ),

  on(getMultipleOrganizationsAction, (state, action): IOrganizationState => {
      return stateHelperFunctions.hasAllItems(state, action.ids) ? state : ({
        ...state,
        loadingOrganizations: state.loadingOrganizations.concat(stateHelperFunctions.getMissingIds(state, action.ids))
      })
    }
  ),

  on(getMultipleOrganizationsSuccessAction, (state, action): IOrganizationState => ({
      ...state,
      loadingOrganizations: stateHelperFunctions.removeLoadingIds(state.loadingOrganizations, action.ids),
      organizations: state.organizations?.concat(action.data)
    })
  ),

  on(getMultipleOrganizationsFailureAction, (state, action): IOrganizationState => ({
      ...state,
      loadingOrganizations: stateHelperFunctions.removeLoadingIds(state.loadingOrganizations, action.ids),
      error: action.error.error
    })
  ),

  on(updateOrganizationSuccessAction, (state, action): IOrganizationState => {
    let organizations = state.organizations;
    let supervisorOrganizations = state.supervisorOrganizations;

    if (!('organizationChanges' in action.data)) {
      const updatedOrganization = action.data;

      organizations = stateHelperFunctions.replaceItemInArray(
        organizations,
        updatedOrganization
      );

      const hasOrganizationInSupervisorList = supervisorOrganizations?.some(
        organization => organization.id === updatedOrganization.id
      );

      if (supervisorOrganizations && hasOrganizationInSupervisorList) {
        supervisorOrganizations = stateHelperFunctions.replaceItemInArray(
          supervisorOrganizations,
          updatedOrganization
        );
      }
    }

    return {
      ...state,
      isUpdating: false,
      isUpdateSuccessful: true,
      loadingOrganizations: stateHelperFunctions.removeLoadingId(state.loadingOrganizations, action.data.id),
      organizations,
      supervisorOrganizations,
      error: undefined
    };
  }),

  on(resetSuccessfulUpdateAction, (state, action): IOrganizationState => ({
    ...state,
    isUpdateSuccessful: false
  })),

  on(approvedOrganizationChangeSuccessAction, (state, action): IOrganizationState => ({
      ...state,
      organizations: stateHelperFunctions.replaceItemInArray(state.organizations, action.data),
      error: undefined
    })
  ),

  on(getSupervisorOrganizationsAction, (state): IOrganizationState => ({
    ...state,
  })),

  on(getSupervisorOrganizationsSuccessAction, (state, action): IOrganizationState => ({
    ...state,
    supervisorOrganizations: action.data,
    error: undefined
  })),

  on(getSupervisorOrganizationsFailureAction, (state, action): IOrganizationState => ({
    ...state,
    supervisorOrganizations: [],
    error: action.error.error
  }))
);

export function organizationReducers(state: IOrganizationState, action: Action) {
  return organizationReducer(state, action);
}
